import React, { useMemo, useState } from "react";
import {
  Box,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { nFormatter } from "../../../Helpers";
import CurrencySymbol from "../../../Components/common/CurrencySymbol";
import WidgetShell from "./WidgetShell";
import {
  EXD,
  ExdEmpty,
  ExdListRow,
  ExdMiniMeter,
  ExdStatItem,
  scrollbarSx,
} from "./exdMui";

const ClientOutstandingListWidget = ({
  loading,
  translate,
  outstandingSummary,
  loginUserCurrency,
  infoTitle,
}) => {
  const [query, setQuery] = useState("");

  const rows = useMemo(() => {
    if (!outstandingSummary?.list) return [];
    return Object.entries(outstandingSummary.list).sort(([, a], [, b]) => b - a);
  }, [outstandingSummary]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(([name]) => String(name || "").toLowerCase().includes(q));
  }, [rows, query]);

  const maxBalance = useMemo(
    () =>
      Math.max(...filteredRows.map(([, balance]) => Number(balance) || 0), 0),
    [filteredRows],
  );

  const severity = (balance) => {
    if (balance > 10000) return "bad";
    if (balance > 4000) return "warn";
    return "neutral";
  };

  return (
    <WidgetShell
      title={translate("Client Outstanding Amounts")}
      infoTitle={infoTitle}
      loading={loading}
      summary={
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ flexWrap: "nowrap", minWidth: 0, width: "100%" }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ flex: 1, flexWrap: "nowrap", minWidth: 0, overflow: "hidden" }}
          >
            <ExdStatItem label="Total outstanding" compact>
              <Tooltip
                title={
                  <Typography sx={{ fontSize: 14 }}>
                    {outstandingSummary?.totalOutstanding?.toLocaleString() || 0}
                  </Typography>
                }
                placement="bottom-start"
                enterDelay={300}
              >
                <Box component="span">
                  {outstandingSummary?.totalOutstanding
                    ? nFormatter(outstandingSummary.totalOutstanding, 2)
                    : 0}{" "}
                  <CurrencySymbol
                    currency={loginUserCurrency}
                    withParentheses={false}
                  />
                </Box>
              </Tooltip>
            </ExdStatItem>
            <ExdStatItem label="Clients" compact>
              {filteredRows.length}
              {query.trim() ? ` / ${rows.length}` : ""}
            </ExdStatItem>
          </Stack>

          <TextField
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={translate("search") || "Search"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 14, color: EXD.muted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: "none",
              width: 120,
              "& .MuiOutlinedInput-root": {
                bgcolor: EXD.surface2,
                borderRadius: "7px",
                fontSize: 11,
                height: 26,
                "& fieldset": { borderColor: EXD.border },
                "&:hover fieldset": { borderColor: EXD.accent },
                "&.Mui-focused fieldset": {
                  borderColor: EXD.accent,
                  borderWidth: 1,
                },
              },
              "& .MuiOutlinedInput-input": {
                py: 0.25,
                px: 0.25,
              },
            }}
          />
        </Stack>
      }
    >
      <Box sx={{ maxHeight: 340, overflowY: "auto", pr: 0.5, ...scrollbarSx }}>
        {filteredRows.length ? (
          filteredRows.map(([name, balance], index) => {
            const value = Number(balance) || 0;
            const sev = severity(value);
            const width = maxBalance ? (value / maxBalance) * 100 : 0;

            return (
              <Tooltip
                key={`${name}-${index}`}
                title={
                  <Typography>
                    {translate("sharedName")} : {name}
                    <br />
                    {translate("totalBalance")}:{" "}
                    {value.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    &nbsp;&nbsp;{" "}
                    <CurrencySymbol
                      currency={loginUserCurrency}
                      withParentheses={false}
                    />
                  </Typography>
                }
                placement="bottom-start"
                enterDelay={300}
              >
                <Box>
                  <ExdListRow
                    rank={String(index + 1).padStart(2, "0")}
                    name={name}
                    meter={<ExdMiniMeter pct={width} tone={sev} />}
                    trailing={
                      <Typography
                        sx={{
                          color: EXD.text,
                          flex: "none",
                          fontFamily: EXD.font,
                          fontSize: 13,
                          fontVariantNumeric: "tabular-nums",
                          fontWeight: 700,
                        }}
                      >
                        {Math.floor(value).toLocaleString()}
                      </Typography>
                    }
                  />
                </Box>
              </Tooltip>
            );
          })
        ) : (
          <ExdEmpty>{translate("No Data") || "No data"}</ExdEmpty>
        )}
      </Box>
    </WidgetShell>
  );
};

export default ClientOutstandingListWidget;
